import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  CreateAccumulationAreaInput,
  Issue,
  IssueLevel,
  UpdateAccumulationAreaInput,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";

import {
  CREATE_ACCUMULATION_AREA_MUTATION,
  GET_ACCUMULATION_AREA_QUERY,
  UPDATE_ACCUMULATION_AREA_MUTATION,
  VALIDATE_ACCUMULATION_AREA_INPUT,
} from "./api";

const TYPE_SELECT_ITEMS = [
  { value: WasteAccumulationAreaType.Satellite, display: "Satellite" },
  { value: WasteAccumulationAreaType.Central, display: "Central" },
];

const TYPE_LOCKED_TOOLTIP =
  "This area has active containers. Move or empty them before changing its type.";

type AccumulationAreaFormValues = {
  name: string;
  type: WasteAccumulationAreaType;
  description: string;
  latitude: string;
  longitude: string;
};

function toAccumulationAreaInput(
  values: AccumulationAreaFormValues,
  facilityId: string
): CreateAccumulationAreaInput {
  return {
    facilityId,
    name: values.name.trim(),
    type: values.type,
    description: values.description.trim() || undefined,
    latitude: safeParseFloat(values.latitude) ?? undefined,
    longitude: safeParseFloat(values.longitude) ?? undefined,
  };
}

function toAccumulationAreaUpdateInput(
  values: AccumulationAreaFormValues,
  areaId: string
): UpdateAccumulationAreaInput {
  return {
    id: areaId,
    name: values.name.trim(),
    type: values.type,
    description: values.description.trim() || null,
    latitude: safeParseFloat(values.latitude),
    longitude: safeParseFloat(values.longitude),
  };
}

/**
 * Debounced live validator, mirroring Containers/WasteContainerDialog's
 * useWasteContainerInputValidation. The area's own name/type must not read
 * as a collision with itself, so currentAreaId is threaded through in edit
 * mode.
 */
function useAccumulationAreaInputValidation(facilityId: string, currentAreaId?: string | null) {
  const [query] = useLazyQuery(VALIDATE_ACCUMULATION_AREA_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (values: AccumulationAreaFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: {
          input: toAccumulationAreaInput(values, facilityId),
          currentAreaId: currentAreaId ?? undefined,
        },
      });
      return data?.accumulationAreaInputValidator ?? [];
    },
    [query, facilityId, currentAreaId]
  );
}

/** Minimal shape callers need back from an inline create (e.g. the
 * container placement picker selecting the just-created area). */
export type CreatedAccumulationArea = {
  id: string;
  name: string;
  type: WasteAccumulationAreaType;
};

export type AccumulationAreaDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Every area belongs to exactly one facility; this locks the create form to it. */
  facilityId: string;
  /** Present in edit mode. The dialog fetches the area itself so callers only need the id. */
  areaId?: string;
  /**
   * Create-mode only: fires after the area is created so an inline-create
   * caller (a placement picker) can select it without a second round-trip.
   */
  onCreated?: (area: CreatedAccumulationArea) => void;
};

export function AccumulationAreaDialog({
  open,
  onClose,
  facilityId,
  areaId,
  onCreated,
}: AccumulationAreaDialogProps) {
  const alerts = useAlerts();
  const mode: "create" | "edit" = areaId ? "edit" : "create";

  const { data, loading: areaLoading } = useQuery(GET_ACCUMULATION_AREA_QUERY, {
    variables: { id: areaId ?? "" },
    skip: !open || !areaId,
  });
  const area = data?.accumulationArea;

  // Type change is create-only once containers are placed: the area's
  // volume rules (satellite thresholds vs. central clock) differ enough
  // that switching type under live containers would leave them in an
  // inconsistent state.
  const typeLocked = mode === "edit" && (area?.containerCount ?? 0) > 0;

  const defaultValues = useMemo<AccumulationAreaFormValues>(
    () => ({
      name: area?.name ?? "",
      type: area?.type ?? WasteAccumulationAreaType.Satellite,
      description: area?.description ?? "",
      latitude: area?.latitude != null ? String(area.latitude) : "",
      longitude: area?.longitude != null ? String(area.longitude) : "",
    }),
    [area]
  );

  const validateInput = useAccumulationAreaInputValidation(facilityId, areaId ?? null);
  const form = useValidatingForm<AccumulationAreaFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    issues,
    formState: { isDirty },
  } = form;

  const [createArea, { loading: isCreating }] = useMutation(CREATE_ACCUMULATION_AREA_MUTATION, {
    // FacilityAccumulationAreaOptions backs the placement pickers (create
    // form, move dialog) that can trigger this dialog inline; refetching it
    // too keeps their option lists current after an inline create.
    refetchQueries: ["FacilityAccumulationAreas", "FacilityAccumulationAreaOptions"],
  });
  const [updateArea, { loading: isUpdating }] = useMutation(UPDATE_ACCUMULATION_AREA_MUTATION, {
    refetchQueries: ["FacilityAccumulationAreas", "GetAccumulationArea"],
  });

  const onSubmit = async () => {
    const values = getValues();
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "create") {
      await createArea({
        variables: { input: toAccumulationAreaInput(values, facilityId) },
        onCompleted: (data) => {
          alerts.success("Area created");
          onCreated?.(data.createAccumulationArea);
          onClose();
        },
        onError: (error) => {
          alerts.error(error.message ?? "An error occurred while creating the area");
        },
      });
      return;
    }
    if (!areaId) {
      return;
    }
    await updateArea({
      variables: { input: toAccumulationAreaUpdateInput(values, areaId) },
      onCompleted: () => {
        alerts.success("Area updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the area");
      },
    });
  };

  const isSaving = isCreating || isUpdating;
  const isLoadingArea = mode === "edit" && areaLoading && !area;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={mode === "create" ? "Add area" : "Edit area"}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        disabled: isLoadingArea || hasCriticalIssues(issues) || !isDirty,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      {isLoadingArea ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <FormProvider {...form}>
          <Box component="form" sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormTextField name="name" label="Name" control={control} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormSelect
                  name="type"
                  label="Type"
                  control={control}
                  selectItems={TYPE_SELECT_ITEMS}
                  disabled={typeLocked}
                  tooltip={typeLocked ? TYPE_LOCKED_TOOLTIP : undefined}
                  clearable={false}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="description"
                  label="Description"
                  control={control}
                  textFieldProps={{ multiline: true, minRows: 2 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormTextField name="latitude" label="Latitude" control={control} type="number" />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormTextField name="longitude" label="Longitude" control={control} type="number" />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      )}
    </Dialog>
  );
}
